<template>
  <SettingsCard
    :title="$t('settings.calendar-sync.title')"
    :subtitle="$t('settings.calendar-sync.subtitle')"
    icon="mdi-calendar-sync"
    :model-value="isOpen"
    @update:model-value="handleToggleOpen"
  >
    <div class="pa-4">
      <p class="text-subtitle-2 font-weight-medium mb-2">
        {{ $t('settings.calendar-sync.connectable-calendars') }}
      </p>
      <v-row class="mb-6">
        <v-col
          cols="12"
          sm="6"
          md="4"
        >
          <GoogleCalendarWrapper
            :feature-disabled="featureDisabled"
            :dialog-activator-props="{ 'data-product-fruits': 'calendar-sync-connect-button' }"
            :on-will-connect="onGoogleCalendarWillConnect"
            :on-will-disconnect="onGoogleCalendarWillDisconnect"
          >
            <template
              #activator="{
                isConnected,
                loading,
                connect,
                disconnect,
                dialogActivatorProps,
                verifiedText,
              }"
            >
              <Connection
                :active="isConnected"
                :image="googleCalendarImage"
                :loading="loading"
                :disabled="featureDisabled"
                :dialog-activator-props="dialogActivatorProps"
                :verified-text="verifiedText"
                :connection-button-text="'settings.calendar-sync.connect-google'"
                @connect="connect"
                @disconnect="disconnect"
              />
            </template>
          </GoogleCalendarWrapper>
        </v-col>
      </v-row>

      <v-divider class="mb-4" />

      <v-alert
        v-if="!connected"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mb-4"
      >
        {{ $t('settings.calendar-sync.connect-hint') }}
      </v-alert>
      <v-alert
        v-if="featureDisabled"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mb-4"
      >
        {{ $t('alerts.upselling.upgrade.2') }}
        <v-btn
          class="mt-2"
          size="small"
          color="primary"
          data-product-fruits="calendar-sync-upgrade-button"
          @click="onUpgradeClick"
        >
          {{ $t('buttons.upgrade-now.cta') }}
        </v-btn>
      </v-alert>

      <GoogleCalendarSyncOptions
        v-if="connected"
        variant="settings"
        intro-title-key="settings.calendar-sync.sync-options"
        intro-description-key="settings.calendar-sync.subtitle"
        :sync-due-dates="Boolean(settings?.syncDueDates)"
        :sync-scheduled-posts="Boolean(settings?.syncScheduledPosts)"
        :sync-calls="callsSyncEnabled"
        :sync-follow-up-calls="Boolean(settings?.syncFollowUpCalls)"
        :disabled="featureDisabled"
        :loading="patchLoading"
        product-fruits-prefix="calendar-sync"
        @update:sync-due-dates="onDueDatesToggle"
        @update:sync-scheduled-posts="onScheduledPostsToggle"
        @update:sync-calls="onCallsToggle"
        @update:sync-follow-up-calls="onFollowUpCallsToggle"
      />
    </div>
  </SettingsCard>
</template>

<script setup lang="ts">
import * as Sentry from '@sentry/vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import googleCalendarImage from '@/assets/img/google-calendar.svg'
import SettingsCard from '@/components/SettingsCard.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import Connection from '@/modules/connectionManager/Connection.vue'
import GoogleCalendarSyncOptions from '@/modules/connectionManager/googleCalendarWrapper/GoogleCalendarSyncOptions.vue'
import GoogleCalendarWrapper from '@/modules/connectionManager/googleCalendarWrapper/index.vue'
import { TrackingPlugin } from '@/plugins/tracking'

import COMPLETE_GOOGLE_CALENDAR_OAUTH from './profileSettings/mutations/CompleteGoogleCalendarOAuth.gql'
import UPDATE_CALENDAR_SYNC_SETTINGS from './profileSettings/mutations/UpdateCalendarSyncSettings.gql'
import CALENDAR_SYNC_SETTINGS from './profileSettings/queries/CalendarSyncSettings.gql'

const props = withDefaults(
  defineProps<{
    featureDisabled?: boolean
  }>(),
  {
    featureDisabled: false,
  }
)

const { isOpen, setOpen } = useQueryOpen({ settingName: 'calendar-sync' })
const { t } = useI18n()
const tracking = inject<TrackingPlugin>('tracking')

const route = useRoute()
const router = useRouter()

const oauthHandling = ref(false)
const patchLoading = ref(false)
const masterSyncIntent = ref<boolean | null>(null)
type CalendarSyncSettings = {
  googleCalendarConnected: boolean
  syncGoogleCalendar: boolean
  syncDueDates: boolean
  syncScheduledPosts: boolean
  syncAiCalls: boolean
  syncTraditionalCalls: boolean
  syncFollowUpCalls: boolean
  followUpDelayDays: number
}

const localSettings = ref<CalendarSyncSettings | null>(null)

const { result, refetch } = useQuery(CALENDAR_SYNC_SETTINGS, null, {
  fetchPolicy: 'cache-and-network',
})

const settings = computed(() => localSettings.value ?? result.value?.calendarSyncSettings ?? null)
const connected = computed(() => Boolean(settings.value?.googleCalendarConnected))

const callsSyncEnabled = computed(() =>
  Boolean(settings.value?.syncAiCalls || settings.value?.syncTraditionalCalls)
)

const { mutate: completeOAuth } = useMutation(COMPLETE_GOOGLE_CALENDAR_OAUTH)
const { mutate: updateSettings } = useMutation(UPDATE_CALENDAR_SYNC_SETTINGS)

type TrackingAction = Parameters<TrackingPlugin['event']>[1]
type TrackingValue = Parameters<TrackingPlugin['event']>[3]

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function applySettingsFromResponse(nextSettings: Partial<CalendarSyncSettings> | null | undefined) {
  if (!nextSettings) return
  const responseSyncGoogleCalendar = Boolean(nextSettings.syncGoogleCalendar)
  const responseSyncDueDates = Boolean(nextSettings.syncDueDates)
  const responseSyncScheduledPosts = Boolean(nextSettings.syncScheduledPosts)
  const responseSyncAiCalls = Boolean(nextSettings.syncAiCalls)
  const responseSyncTraditionalCalls = Boolean(nextSettings.syncTraditionalCalls)
  const responseSyncFollowUpCalls = Boolean(nextSettings.syncFollowUpCalls)
  const responseHasEnabledChild =
    responseSyncDueDates ||
    responseSyncScheduledPosts ||
    responseSyncAiCalls ||
    responseSyncTraditionalCalls ||
    responseSyncFollowUpCalls
  const responseInconsistent = !responseSyncGoogleCalendar && responseHasEnabledChild
  const syncGoogleCalendar =
    responseInconsistent && masterSyncIntent.value !== null
      ? masterSyncIntent.value
      : responseSyncGoogleCalendar
  const forceChildrenOff = !syncGoogleCalendar

  if (!responseInconsistent) {
    masterSyncIntent.value = null
  }

  localSettings.value = {
    googleCalendarConnected: Boolean(nextSettings.googleCalendarConnected),
    syncGoogleCalendar,
    syncDueDates: forceChildrenOff ? false : responseSyncDueDates,
    syncScheduledPosts: forceChildrenOff ? false : responseSyncScheduledPosts,
    syncAiCalls: forceChildrenOff ? false : responseSyncAiCalls,
    syncTraditionalCalls: forceChildrenOff ? false : responseSyncTraditionalCalls,
    syncFollowUpCalls: forceChildrenOff ? false : responseSyncFollowUpCalls,
    followUpDelayDays: Number(nextSettings.followUpDelayDays) || 1,
  }
}

function trackInteraction(action: TrackingAction, label: string, value?: TrackingValue) {
  tracking?.event('Settings', action, `Calendar Sync - ${label}`, value)
}

function handleToggleOpen(open: boolean) {
  setOpen()
  trackInteraction(
    'Clicked',
    'calendar_card_toggle',
    JSON.stringify({
      open,
      state: open ? 'open' : 'closed',
      cardId: 'calendar-sync',
      cardName: 'Calendar Sync',
    })
  )
}

function onGoogleCalendarWillConnect() {
  trackInteraction('Clicked', 'Connect Google Calendar')
}

function onGoogleCalendarWillDisconnect() {
  masterSyncIntent.value = null
  trackInteraction('Clicked', 'Disconnect Google Calendar')
}

function withDerivedMasterSync(
  input: Partial<CalendarSyncSettings>
): Partial<CalendarSyncSettings> {
  const current = settings.value
  if (!current) return input

  const next = {
    syncDueDates: current.syncDueDates,
    syncScheduledPosts: current.syncScheduledPosts,
    syncAiCalls: current.syncAiCalls,
    syncTraditionalCalls: current.syncTraditionalCalls,
    syncFollowUpCalls: current.syncFollowUpCalls,
    ...input,
  }
  const anyChild =
    next.syncDueDates ||
    next.syncScheduledPosts ||
    next.syncAiCalls ||
    next.syncTraditionalCalls ||
    next.syncFollowUpCalls

  return {
    ...input,
    syncGoogleCalendar: anyChild,
  }
}

async function patchSettings(input: Partial<CalendarSyncSettings>) {
  if (props.featureDisabled) return
  const current = settings.value
  if (!current) return

  const derivedInput = withDerivedMasterSync(input)
  const payload = {
    syncGoogleCalendar: current.syncGoogleCalendar,
    syncDueDates: current.syncDueDates,
    syncScheduledPosts: current.syncScheduledPosts,
    syncAiCalls: current.syncAiCalls,
    syncTraditionalCalls: current.syncTraditionalCalls,
    syncFollowUpCalls: current.syncFollowUpCalls,
    followUpDelayDays: current.followUpDelayDays,
    ...derivedInput,
  }

  const previousSettings = localSettings.value ? { ...localSettings.value } : null
  applySettingsFromResponse({ ...current, ...derivedInput })

  patchLoading.value = true
  try {
    const { data } = await updateSettings({ input: payload })
    applySettingsFromResponse(data?.updateCalendarSyncSettings)
    await refetch()
  } catch (e: unknown) {
    localSettings.value = previousSettings
    const error = normalizeError(e)
    console.error('[CalendarSettings] Failed to update calendar sync settings', error)
    Sentry.captureException(error, {
      tags: { component: 'CalendarSettings', action: 'updateCalendarSyncSettings' },
    })
    showSnackbarMessage('error', t('settings.calendar-sync.messages.update-failed'))
  } finally {
    patchLoading.value = false
  }
}

function onDueDatesToggle(value: boolean) {
  trackInteraction('Clicked', 'Due dates toggle', value ? 'Enable' : 'Disable')
  patchSettings({ syncDueDates: value })
}

function onScheduledPostsToggle(value: boolean) {
  trackInteraction('Clicked', 'Scheduled posts toggle', value ? 'Enable' : 'Disable')
  patchSettings({ syncScheduledPosts: value })
}

function onCallsToggle(value: boolean) {
  trackInteraction('Clicked', 'Calls toggle', value ? 'Enable' : 'Disable')
  patchSettings({ syncAiCalls: value, syncTraditionalCalls: value })
}

function onFollowUpCallsToggle(value: boolean) {
  trackInteraction('Clicked', 'Follow-up calls toggle', value ? 'Enable' : 'Disable')
  patchSettings({ syncFollowUpCalls: value })
}

function onUpgradeClick() {
  trackInteraction('Clicked', 'Upgrade now')
  void router.push({
    name: 'Settings',
    params: { section: 'contract' },
    query: { showUpgrade: '1' },
  })
}

async function handleOAuthReturn() {
  if (oauthHandling.value) return
  if (route.name !== 'Settings' || route.params.section !== 'profile') return

  const code = typeof route.query.code === 'string' ? route.query.code.trim() : null
  const state = typeof route.query.state === 'string' ? route.query.state.trim() : null
  if (!code || !state) return

  oauthHandling.value = true
  masterSyncIntent.value = null
  try {
    const { data } = await completeOAuth({ input: { code, state } })
    applySettingsFromResponse(data?.completeGoogleCalendarOAuth)
    await refetch()
    showSnackbarMessage('success', t('settings.calendar-sync.messages.connected'))
  } catch (e: unknown) {
    const error = normalizeError(e)
    console.error('[CalendarSettings] Failed to complete Google Calendar OAuth', error)
    Sentry.captureException(error, {
      tags: { component: 'CalendarSettings', action: 'completeGoogleCalendarOAuth' },
    })
    showSnackbarMessage('error', t('settings.calendar-sync.messages.auth-failed'))
  } finally {
    await router.replace({
      name: 'Settings',
      params: { section: 'profile' },
      query: { 'settings-open': 'calendar-sync' },
    })
    oauthHandling.value = false
  }
}

watch(
  () => result.value?.calendarSyncSettings,
  (nextSettings) => {
    applySettingsFromResponse(nextSettings)
  },
  { immediate: true }
)

watch(
  () => [route.query.code, route.query.state, route.params.section],
  () => {
    void handleOAuthReturn()
  },
  { immediate: true }
)
</script>
